/**
 * web-studio-sites 6.6 (design D15; Gabriel 2026-10-26: "and yes with marks in the lists"): a signed-in client's own
 * marks in the lists of a page, with the mine option.
 * - An event's mark comes from the signed-in events list's own `userBooking`, which names the exact event. One waiting
 *   on payment takes the rest from the dashboard's copy of that booking, matched by its id.
 * - A service's comes from the client's upcoming bookings in the dashboard read: the next one of it.
 * - A mark names where its booking stands; its button is the booking's own step (bookingErrand, at the website's
 *   addresses) or View booking.
 */
import type { SiteEventSummaryDto } from '@telify/shared';
import type { BookingDetailStatus } from '@/types/bookingDetail';
import type { UpcomingBooking } from '@/types/dashboard';

import { formatDayLabel, formatSlotTime } from './format';
import { bookingPageErrand } from './pieces/booking-page/useBookingPage';
import type { MineMark, MineState } from './pieces/events-row/contract';

/** Each mark's words (the Dana thread, bookings.html at 96d442a62): an event's line, and on a service the words before its time. */
const WORDS: Readonly<Record<MineState, { readonly event: string; readonly service: string }>> = {
  going: { event: "You're going", service: 'Your next' },
  held: { event: 'Booked for you', service: 'Booked for you' },
  approved: { event: 'Approved', service: 'Approved' },
  requested: { event: 'Requested', service: 'Requested' },
  waitlisted: { event: "You're on the waitlist", service: 'On the waitlist' },
};

const VIEW_BOOKING = 'View booking';

/** The dashboard's upcoming bookings as an event's mark reads them: loaded, still loading (or not asked for), or failed. */
export type DashboardCopies = readonly UpcomingBooking[] | 'loading' | 'failed';

/** A client's own booking as a mark reads it: the dashboard's copy, or an event's `userBooking`. */
interface Own {
  readonly id: string;
  readonly status: string;
  readonly completeBy?: string | null;
  readonly outstanding?: UpcomingBooking['outstanding'];
  readonly payment?: UpcomingBooking['payment'];
  readonly service: Pick<UpcomingBooking['service'], 'id' | 'name' | 'kind'>;
}

/**
 * Where a booking stands; null for one that marks nothing. A booking the studio made (`completeBy`) is held, whether it
 * waits on the client or its forms are under the studio's review; a request the studio approved that still waits on
 * payment is approved.
 */
function stateOf(b: Pick<Own, 'status' | 'completeBy'>): MineState | null {
  if (b.status === 'CONFIRMED') return 'going';
  if (b.status === 'PENDING_PAYMENT') return b.completeBy ? 'held' : 'approved';
  if (b.status === 'PENDING_APPROVAL') return b.completeBy ? 'held' : 'requested';
  if (b.status === 'WAITLISTED') return 'waitlisted';
  return null;
}

const bookingPath = (id: string) => `/account/bookings/${encodeURIComponent(id)}`;
const whenOf = (startsAt: string, tz: string) => `${formatDayLabel(startsAt, tz)} at ${formatSlotTime(startsAt, tz)}`;

function markOf(b: Own, startsAt: string, words: 'event' | 'service', tz: string): MineMark | null {
  const state = stateOf(b);
  if (!state) return null;
  const errand = bookingPageErrand({
    id: b.id,
    // One of the four upcoming statuses, stateOf having named it.
    status: b.status as BookingDetailStatus,
    completeBy: b.completeBy ?? null,
    outstanding: b.outstanding ?? null,
    payment: b.payment ?? null,
    // Whether it is an event is all the rule reads of the type (Book again), and an upcoming booking never books again.
    service: { id: b.service.id, name: b.service.name, type: b.service.kind === 'event' ? 'event' : 'individual' },
  });
  const href = bookingPath(b.id);
  return {
    bookingId: b.id,
    state,
    label: WORDS[state][words],
    startsAt,
    when: whenOf(startsAt, tz),
    href,
    action: errand ? { label: errand.label, href: errand.href } : { label: VIEW_BOOKING, href },
  };
}

/**
 * Each listed service the client has booked, by its id: the soonest of their bookings of it that has not started and
 * is not cancelled. A booking on an event never marks a service (it carries the event's template service).
 */
export function serviceMarks(upcoming: readonly UpcomingBooking[], now: Date, tz: string): Record<string, MineMark> {
  const next = new Map<string, UpcomingBooking>();
  for (const b of upcoming) {
    const at = Date.parse(b.scheduledAt);
    if (b.service.kind !== 'service' || !(at > now.getTime()) || !stateOf(b)) continue;
    const soonest = next.get(b.service.id);
    if (!soonest || at < Date.parse(soonest.scheduledAt)) next.set(b.service.id, b);
  }
  const marks: Record<string, MineMark> = {};
  for (const [serviceId, b] of next) marks[serviceId] = markOf(b, b.scheduledAt, 'service', tz)!;
  return marks;
}

/**
 * The statuses whose state hangs on whether the studio made the booking (`completeBy`), which an event's own
 * `userBooking` does not carry: waiting on payment (held or approved) and waiting on the studio (held or requested).
 */
const COPY_NEEDED = new Set(['PENDING_PAYMENT', 'PENDING_APPROVAL']);

/** True when a listed event's own booking needs the dashboard's copy of it for its mark. */
export function needsDashboardCopy(events: readonly SiteEventSummaryDto[]): boolean {
  return events.some((e) => COPY_NEEDED.has(e.userBooking?.status ?? ''));
}

/**
 * Each listed event the client has a booking on, by its id, from the events list's own `userBooking`. One waiting on
 * payment or on the studio takes the rest from the dashboard's copy of it. A class with a booking of the client's never
 * offers Book: while the copy loads, or when it failed or lacks the booking, the mark is View booking in Book's place
 * with no state, never a guess.
 */
export function eventMarks(events: readonly SiteEventSummaryDto[], copies: DashboardCopies, tz: string): Record<string, MineMark> {
  const marks: Record<string, MineMark> = {};
  for (const e of events) {
    const own = e.userBooking;
    if (!own) continue;
    if (!COPY_NEEDED.has(own.status)) {
      const mark = markOf({ ...own, service: { id: e.id, name: e.title, kind: 'event' } }, e.startTime, 'event', tz);
      if (mark) marks[e.id] = mark;
      continue;
    }
    const copy = Array.isArray(copies) ? copies.find((b) => b.id === own.id) : undefined;
    const href = bookingPath(own.id);
    marks[e.id] = (copy && markOf(copy, e.startTime, 'event', tz)) || {
      bookingId: own.id,
      state: null,
      label: null,
      startsAt: e.startTime,
      when: whenOf(e.startTime, tz),
      href,
      action: { label: VIEW_BOOKING, href },
    };
  }
  return marks;
}
